import { Injectable } from '@angular/core';
import { AvisosService } from '@core';
import { KeycloakService } from 'keycloak-angular';
import { ERROR_MESSAGES, type SupportedLanguage, type HttpStatusCode } from '@core/i18n';

/**
 * ErrorService - Servicio centralizado para manejo de errores HTTP
 * 
 * Características:
 * - Maneja errores HTTP de forma centralizada desde el interceptor
 * - Soporte de mensajes en español e inglés
 * - Detección automática del idioma del navegador para mensajes fallback
 * - Prioriza mensajes del backend sobre mensajes locales
 * - Muestra avisos visuales al usuario mediante AvisosService
 * 
 * Orden de prioridad para mensajes:
 * 1. error.error.userMessage (mensaje específico del backend - máxima prioridad)
 * 2. error.error.message (mensaje genérico del backend - tuberías usa este campo)
 * 3. ERROR_MESSAGES[idioma][código] (mensajes locales según idioma del navegador)
 * 
 * Funcionamiento del idioma:
 * - Si navigator.language empieza con 'en' → usa mensajes en inglés
 * - Cualquier otro idioma → usa mensajes en español (por defecto)
 * - Los mensajes del backend (1 y 2) siempre se respetan independientemente del idioma
 */

export interface ErrorResponse {
    status: number;
    error: {
        code?: string;
        userMessage?: string;
        message?: string;
        error?: { message?: string };
        fields?: string[];
        errors?: { message: string }[];
    };
}

@Injectable({
    providedIn: 'root',
})
export class ErrorService {
    constructor(
        private avisosService: AvisosService,
        private keycloakService: KeycloakService,
    ) {}

    /**
     * @description All the errors in HTTP Requests came here from the http-loading-interceptor file.
     *  This function decides which error handling function to call based on its status code.
     * @param error The error from the request
     */

    private errorHandlers: { [key: number]: (error: ErrorResponse) => void } = {
        400: this.handle400Errors.bind(this), // BAD REQUEST
        401: this.handle401Errors.bind(this), // UNAUTHORIZED
        402: this.handle402Errors.bind(this), // PAY REQUIRED
        403: this.handle403Errors.bind(this), // FORBIDDEN
        404: this.handle404Errors.bind(this), // NOT FOUND
        408: this.handle408Errors.bind(this), // REQUEST TIMEOUT
        409: this.handle409Errors.bind(this), // CONFLICT
        500: this.handle500Errors.bind(this), // INTERNAL SERVER ERROR
        503: this.handle503Errors.bind(this), // SERVICE UNAVAILABLE
    };

    handleError(error: ErrorResponse) {
        console.log('error', error);

        const handler = this.errorHandlers[error.status];
        if (handler) {
            handler(error);
        }
    }

    /**
     * @description Detecta el idioma preferido del navegador
     * @returns 'es' para español (por defecto) o 'en' para inglés
     */
    private getCurrentLanguage(): SupportedLanguage {
        const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage;
        return browserLang?.startsWith('en') ? 'en' : 'es';
    }

    /**
     * @description Get error message with fallback priority:
     * 1. Backend userMessage (highest priority)
     * 2. Backend message (used by tuberías with English messages)
     * 3. Backend nested error.message (used by faltas API: {"error": {"message": "..."}})
     * 4. Local default message in appropriate language (based on browser language)
     */
    private getUserMessage(error: ErrorResponse, statusCode: HttpStatusCode): string {
        // Prioridad 1: Mensaje específico del backend
        if (error.error?.userMessage) {
            return error.error.userMessage;
        }
        
        // Prioridad 2: Mensaje genérico del backend (tuberías usa este campo en inglés)
        if (error.error?.message) {
            return error.error.message;
        }

        // Prioridad 3: Formato anidado del API de faltas: { "error": { "message": "..." } }
        if (error.error?.error?.message) {
            return error.error.error.message;
        }

        // Prioridad 4: Mensaje local en el idioma del navegador
        const lang = this.getCurrentLanguage();
        const messages = ERROR_MESSAGES[lang];
        return messages[statusCode] || messages['500'];
    }

    private showAviso(mensaje: string, tipo: 'success' | 'info' | 'warn' | 'error') {
        this.avisosService.agregarAviso(mensaje, tipo);
    }

    /**
     * @description Handle the errors with status 400
     * @param error The error from the request
     */
    handle400Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '400');

        this.showAviso(userMessage, 'error');
        // Mostrar validaciones del array fields si existen
        if (error.error?.fields && Array.isArray(error.error.fields)) {
            error.error.fields.forEach(fieldMessage => {
                this.showAviso(fieldMessage, 'warn');
            });
        }
    }

    /**
     * @description Handle the errors with status 401
     * @param error The error from the request
     */
    handle401Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '401');
        this.showAviso(userMessage, 'error');

        // Redirigimos al usuario al login de Keycloak después de mostrar el mensaje
        setTimeout(() => {
            this.keycloakService.login();
        }, 2000); // Espera 2 segundos para que el usuario pueda ver el mensaje
    }

    /**
     * @description Handle the errors with status 402
     * @param error The error from the request
     */
    handle402Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '402');
        this.showAviso(userMessage, 'warn');
    }

    /**
     * @description Handle the errors with status 403
     * @param error The error from the request
     */
    handle403Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '403');

        this.showAviso(userMessage, 'error');
    }

    /**
     * @description Handle the errors with status 404
     * @param error The error from the request
     */
    handle404Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '404');

        this.showAviso(userMessage, 'warn');
    }

    /**
     * @description Handle the errors with status 408
     * @param error The error from the request
     */
    handle408Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '408');
        this.showAviso(userMessage, 'warn');
    }

    /**
     * @description Handle the errors with status 409
     * @param error The error from the request
     */
    handle409Errors(error: ErrorResponse & { url?: string }) {
        // No mostrar aviso automático para errores de sistemas/subsistemas
        // El componente maneja estos errores con un mensaje personalizado
        const url = error?.url || '';
        const isSistemaOrSubsistema = url.includes('/sistemas') || url.includes('/subsistemas');

        if (isSistemaOrSubsistema) {
            // No mostrar nada, el componente lo maneja
            console.log('Error 409 en sistema/subsistema - manejado por el componente');
            return;
        }

        const userMessage = this.getUserMessage(error, '409');

        this.showAviso(userMessage, 'error');
    }

    /**
     * @description Handle the errors with status 500
     * @param error The error from the request
     */
    handle500Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '500');
        this.showAviso(userMessage, 'error');
    }

    /**
     * @description Handle the errors with status 503
     * @param error The error from the request
     */
    handle503Errors(error: ErrorResponse) {
        const userMessage = this.getUserMessage(error, '503');
        this.showAviso(userMessage, 'error');
    }
}
